import React, { useState } from 'react';
import './ProductPlan.css';

/* ══════════════════════════════════════════════════════════════════════════ */
/*  DATA                                                                      */
/* ══════════════════════════════════════════════════════════════════════════ */

/* ── DepGuard Platform ─────────────────────────────────────────────────────── */
const DEPGUARD_DONE = [
  { id: 'dg-1',  icon: '🔐', title: 'JWT Auth + RBAC + Audit log', desc: 'Admin / Analyst / Viewer roles with full audit trail.' },
  { id: 'dg-2',  icon: '⬆️', title: 'SBOM upload (SPDX-JSON + CycloneDX-JSON)', desc: 'Drag-and-drop upload for both major SBOM formats.' },
  { id: 'dg-3',  icon: '🔍', title: 'Vulnerability scanning via OSV/GHSA', desc: 'Real-time CVE lookups with SQLite persistence.' },
  { id: 'dg-4',  icon: '🏥', title: 'Package health (OpenSSF Scorecard)', desc: 'Abandoned detection, license, age, and health scores.' },
  { id: 'dg-5',  icon: '📊', title: 'SBOM quality analysis', desc: '5 dimensions, NTIA compliance, grade A–F.' },
  { id: 'dg-6',  icon: '⚖️', title: 'License analysis + risk categorization', desc: 'Automated license classification and risk flagging.' },
  { id: 'dg-7',  icon: '📋', title: 'Policy engine', desc: 'Preset + custom policies with CI/CD gate endpoint.' },
  { id: 'dg-8',  icon: '🤖', title: 'AI assistant (multi-provider)', desc: 'Copilot, GitHub Models, OpenAI, Anthropic, Ollama.' },
  { id: 'dg-9',  icon: '📈', title: 'Trend analysis', desc: 'Risk score over time with Recharts line charts.' },
  { id: 'dg-10', icon: '🔀', title: 'SBOM diff', desc: 'Added / removed / changed packages across versions.' },
  { id: 'dg-11', icon: '✅', title: 'VEX statements', desc: 'Status, justification, FixedIn badges, CycloneDX VEX export.' },
  { id: 'dg-12', icon: '🕸️', title: 'Dependency graph', desc: 'Interactive D3 force-graph with CycloneDX dependsOn.' },
  { id: 'dg-13', icon: '🛠️', title: 'Remediation tracking', desc: 'Status, owner, due date, overdue alerts.' },
  { id: 'dg-14', icon: '👴', title: 'Abandoned package detection', desc: 'OpenSSF Scorecard + GitHub archived API; threshold: no commits > 2yr.' },
  { id: 'dg-15', icon: '🗺️', title: 'Compliance framework mapping', desc: 'NIST SP 800-53, ISO 27001, EU CRA.' },
  { id: 'dg-16', icon: '📄', title: 'PDF report export', desc: 'Browser print via HTML template + Go report generation.' },
  { id: 'dg-17', icon: '⚙️', title: 'GitHub Actions CI/CD wizard', desc: '3-step UX, 6 ecosystems, policy gate.' },
  { id: 'dg-18', icon: '🔔', title: 'Webhook alerts (Slack/Teams)', desc: '3 types, 4 event types, HMAC signing, delivery logs.' },
  { id: 'dg-19', icon: '⏳', title: 'Background async job queue', desc: '/jobs/:id and /jobs/repo/:repoName endpoints.' },
  { id: 'dg-20', icon: '🇪🇺', title: 'EU CRA Compliance Report', desc: '11 Article 20 checks, audit doc, portfolio score.' },
  { id: 'dg-21', icon: '🔗', title: 'PURL/CPE Enrichment & Validation', desc: 'Auto-enrich SBOMs with PURL and CPE identifiers.' },
  { id: 'dg-22', icon: '☠️', title: 'Malware & Typosquatting Detection v2', desc: 'Jaro-Winkler, Damerau-Levenshtein, combosquatting, Unicode — npm 400+, PyPI 300+.' },
  { id: 'dg-23', icon: '🔏', title: 'SLSA Provenance Verification (MVP)', desc: 'Attestation upload, digest match, level estimation.' },
  { id: 'dg-24', icon: '👤', title: 'Maintainer Anomaly Detection', desc: 'Anomaly scores, severity, package flags.' },
  { id: 'dg-25', icon: '📡', title: 'Reachability Analysis', desc: '6-tier pipeline — Tier 3: static grep, Tier 4: lockfile depth, Tier 5: Joern CPG, Tier 6: Semgrep.' },
  { id: 'dg-26', icon: '⚡', title: 'Vigil SAST tab in RepoDetails', desc: 'Scan history, findings table, re-scan trigger.' },
  { id: 'dg-27', icon: '🤝', title: 'Supplier Trust Portfolio', desc: 'Combined malware + maintainer + abandoned + reachability + SLSA.' },
  { id: 'dg-28', icon: '📦', title: 'Evidence Pack Export', desc: 'ZIP with SBOM, CRA doc, remediation, reachability, SLSA, audit log.' },
  { id: 'dg-29', icon: '🔑', title: 'OIDC / Azure AD SSO v1', desc: 'Enterprise sign-in, auto-provisioning, RBAC-bridging.' },
  { id: 'dg-30', icon: '🎫', title: 'Jira Integration v1', desc: 'Configure Jira, open tickets from remediations, status sync.' },
  { id: 'dg-31', icon: '💳', title: 'Pricing page + trial flow', desc: 'Self-serve trial onboarding and pricing tiers.' },
  { id: 'dg-32', icon: '🧠', title: 'AI Assistant upgrade — rich context', desc: 'Auto-generates health summary when panel opens. Full context sent to LLM: all CVEs, licenses, quality, remediation. Risk score bar, CVE cards, license pills, CRA banner. 5 suggested questions. NL queries scoped to current repo + SBOM.' },
  { id: 'dg-33', icon: '⚡', title: 'Shared data architecture', desc: 'Overview tab preloads all 6 data endpoints in parallel (vulns, health, licenses, quality, compliance, remediation). Child tabs (Vulnerabilities, Licenses, Package Health, SBOM Quality) reuse parent data — zero double-fetching.' },
  { id: 'dg-34', icon: '📋', title: 'Remediation card in Overview', desc: 'Overview tab now includes a Remediation summary card showing open / overdue / suppressed counts with direct link to Remediation tab.' },
  { id: 'dg-35', icon: '🌐', title: 'Cross-Project Dependency Impact Graph', desc: 'Interactive force-graph at /impact showing which projects are affected by a vulnerable package. Graph/table view toggle, sortable table, CVE click-through, CSV export, pulse animation on CRITICAL nodes.' },
  { id: 'dg-36', icon: '📋', title: 'CSAF Advisory Support', desc: 'Import CSAF 2.0 advisories from Microsoft, Red Hat, etc. Two matching strategies: CVE cross-reference + normalized name matching. Per-SBOM CSAF matches tab in Vulnerabilities.' },
];

const DEPGUARD_PLANNED = [
  {
    id: 'dg-p1', icon: '🦾', title: 'Autonomous AI Security Agent', priority: 'critical',
    desc: 'Agent that automatically triages new CVEs against all SBOMs, proposes VEX statements, opens remediation items, and sends alerts — without human input. 80% reduction in manual security work.',
    stories: [
      'As a security analyst, I want CVEs to be automatically triaged against my SBOMs so that I don\'t have to manually review every new advisory.',
      'As a security officer, I want the AI agent to propose VEX justifications so that I can approve them in one click rather than writing them manually.',
      'As a DevOps engineer, I want automatic remediation tickets created for critical CVEs so that the team is notified without waiting for a security review.',
    ],
    criteria: [
      'New CVEs detected within 1 hour of OSV publication.',
      'VEX proposals include justification text with ≥80% acceptance rate.',
      'Remediation items opened automatically with correct severity and owner suggestion.',
    ],
  },
  {
    id: 'dg-p2', icon: '🏢', title: 'SAML / LDAP enterprise identity hardening', priority: 'high',
    desc: 'OIDC/Azure AD v1 is live; needs broader directory coverage (SAML 2.0, LDAP, Okta, PingIdentity) + enterprise security hardening.',
    stories: [
      'As an enterprise IT admin, I want SAML 2.0 support so that DepGuard integrates with our existing identity provider.',
      'As a security officer, I want LDAP group mapping so that our AD groups automatically become DepGuard roles.',
    ],
    criteria: [
      'SAML 2.0 SP-initiated login works with Okta, Azure AD, PingIdentity.',
      'LDAP group → RBAC role mapping configurable in admin settings.',
    ],
  },
  {
    id: 'dg-p3', icon: '🐳', title: 'IaC & Container SBOM Support', priority: 'high',
    desc: 'Support SBOMs from Terraform, Helm, Docker images (Syft already supports these formats). One platform for code + infra + containers.',
    stories: [
      'As a DevOps engineer, I want to upload SBOMs for Docker images so that I can track vulnerabilities in container dependencies.',
      'As a platform engineer, I want Terraform module SBOMs so that my infrastructure dependencies are part of the supply chain picture.',
    ],
    criteria: [
      'Docker image SBOMs (Syft-generated CycloneDX) upload and parse correctly.',
      'Terraform module dependencies appear in the dependency graph.',
    ],
  },
  {
    id: 'dg-p4', icon: '📡', title: 'Reachability Analysis hardening', priority: 'high',
    desc: 'MVP is live (6-tier), needs production hardening: more languages (Python, JS/TS, Go), better precision, lower false positive rate.',
    stories: [
      'As a developer, I want reachability analysis for Python projects so that I know which CVEs in my pip dependencies are actually exploitable.',
      'As a security analyst, I want fewer false positives in reachability so that I can trust the "reachable" verdict.',
    ],
    criteria: [],
  },
  {
    id: 'dg-p5', icon: '🔧', title: 'ServiceNow & broader workflow integrations', priority: 'high',
    desc: 'Jira v1 is live; large enterprise teams need ITSM integrations (ServiceNow, Linear, Azure DevOps Boards).',
    stories: [
      'As an enterprise security officer, I want ServiceNow integration so that security findings flow into our existing ITSM process.',
    ],
    criteria: [],
  },
  {
    id: 'dg-p5b', icon: '🌱', title: 'Community Health Intelligence', priority: 'high',
    desc: '7 sociotechnical metrics per dependency pulled from GitHub API: Bus Factor, Burstiness Metric Index (BMI), Responsiveness Efficiency Index (REI), Median Issue Lead Time, Median PR Lead Time, Retention Rate, Growth of Active Contributors. Community risk score 0–10 per package, grouped in 3 categories: Demand vs. Capacity, Response Speed, Community Sustainability.',
    stories: [
      'As a security analyst, I want to see the Bus Factor for each dependency so that I know which packages are maintained by a single person who could disappear.',
      'As a compliance officer, I want to flag dependencies with BMI < 1 (more issues opened than closed) so that I can identify packages going into maintenance decline.',
      'As a developer, I want to see contributor growth trends so that I know if a package community is growing or shrinking.',
    ],
    criteria: [
      'All 7 metrics computed from GitHub REST API (issues, PRs, commits).',
      'Community risk score 0–10 displayed per package in Package Health view.',
      'Packages grouped by 3 categories in dedicated Community Health sub-section.',
      'Builds on existing fetchGitHubContributorCount() in depsdev_service.go.',
    ],
  },
  {
    id: 'dg-p5c', icon: '🏢', title: 'Corporate Backing Analysis', priority: 'high',
    desc: 'Identify which organizations (by GitHub org of contributors) dominate each package maintenance. Shows single-vendor flag (relicensing risk), organization dominance score, and detection of corporate exits/entries. Directly relevant for EU CRA due diligence on supply chain transparency.',
    stories: [
      'As a CISO, I want to know if a critical dependency is controlled by a single company so that I can assess relicensing risk.',
      'As a compliance officer, I want to detect when a major corporate backer withdraws from a project so that I can flag it as a supply chain risk.',
    ],
    criteria: [
      'Org dominance score per package (% commits from top org).',
      'Single-vendor flag displayed when top org > 80% of commits.',
      'Corporate exit alert when dominant org drops > 30% of contribution share.',
    ],
  },
  {
    id: 'dg-p5d', icon: '🔄', title: 'Competitive Dependency Analysis', priority: 'high',
    desc: 'When a dependency scores high risk on community health, automatically suggest healthier alternatives that provide the same functionality. Differentiates DepGuard from all existing SBOM tools.',
    stories: [
      'As a developer, I want DepGuard to suggest a healthier alternative to a high-risk dependency so that I know my options without manual research.',
      'As a security analyst, I want to see a comparison of the current package vs. its top alternative so that I can present a clear recommendation to the team.',
    ],
    criteria: [
      'Alternatives suggested for packages scoring community risk > 7.',
      'Alternative comparison shows: health score, CVE count, license, last commit date, weekly downloads.',
      'One-click "Switch to alternative" that creates a remediation item.',
    ],
  },
  {
    id: 'dg-p6', icon: '🐘', title: 'PostgreSQL support', priority: 'medium',
    desc: 'SQLite blocks multi-tenant SaaS scaling (SetMaxOpenConns(1) is a hard blocker). Add PostgreSQL alongside SQLite.',
    stories: [
      'As a platform operator, I want PostgreSQL support so that I can run DepGuard as a multi-tenant SaaS with concurrent users.',
    ],
    criteria: [
      'All SQLite queries migrated to PostgreSQL-compatible SQL.',
      'pgx driver + connection pooling configured.',
      'Performance: 10× throughput vs SQLite at 100 concurrent users.',
    ],
  },
  {
    id: 'dg-p7', icon: '🏢', title: 'Multi-organisation API isolation', priority: 'medium',
    desc: 'DB schema has org_id partially in place; needs full API-level isolation: org-scoped repos, policies, webhooks, and user management.',
    stories: [
      'As a platform operator, I want org-level data isolation so that customer A cannot see customer B\'s SBOMs.',
    ],
    criteria: [],
  },
];

/* ── Vigil SAST Engine ────────────────────────────────────────────────────── */
const VIGIL_DONE = [
  { id: 'vg-1',  icon: '🔍', title: 'Core pattern engine (OpenGrep/Semgrep)', desc: 'Primary AST-aware pattern matching backend.' },
  { id: 'vg-2',  icon: '🕸️', title: 'Joern CPG engine', desc: 'Taint flow, call graph, cross-file analysis via Joern.' },
  { id: 'vg-3',  icon: '🌲', title: 'Tree-sitter + Rascal adapters', desc: 'Pluggable language parsing adapters.' },
  { id: 'vg-4',  icon: '🎛️', title: 'Multi-engine orchestration', desc: 'Confidence-based merge across engines.' },
  { id: 'vg-5',  icon: '📜', title: '110+ rule packs across 10 languages', desc: 'Comprehensive OWASP-aligned rule coverage.' },
  { id: 'vg-6',  icon: '🔀', title: 'Cross-file taint flow (find crossflow)', desc: 'Inter-procedural taint tracking across module boundaries.' },
  { id: 'vg-7',  icon: '💡', title: 'Concept catalog', desc: '50 concepts, 351 language mappings.' },
  { id: 'vg-8',  icon: '📝', title: 'Vigil DSL (full grammar)', desc: 'pattern, flow, call, usage, literal, import, annotation, class, variable, assignment, crossflow.' },
  { id: 'vg-9',  icon: '📤', title: 'SARIF export', desc: 'Industry-standard Static Analysis Results Interchange Format.' },
  { id: 'vg-10', icon: '🛠️', title: 'Rule Studio (web-based editor)', desc: 'Rule editor with live testing and syntax highlighting.' },
  { id: 'vg-11', icon: '🎓', title: 'Vigil Academy', desc: '8 lessons, modules 1–3 complete.' },
  { id: 'vg-12', icon: '🔇', title: 'Baseline + suppression system', desc: 'Suppress known findings and track baselines over time.' },
  { id: 'vg-13', icon: '⏳', title: 'Background async scan jobs', desc: 'Non-blocking scan execution with job status polling.' },
  { id: 'vg-14', icon: '🏗️', title: 'Kotlin + Terraform rule packs', desc: 'Latest language coverage additions.' },
  { id: 'vg-15', icon: '🗂️', title: 'Language grouping in pack selector UI', desc: 'Organized rule pack selection by language family.' },
];

const VIGIL_COVERAGE = [
  { name: 'juice-shop', current: 77.4, target: 80, findings: '6,641/8,579' },
  { name: 'railsgoat',  current: 22,   target: 25, findings: '~22%' },
  { name: 'WebGoat',    current: 46,   target: 60, findings: '~46%' },
  { name: 'NodeGoat',   current: 40,   target: 50, findings: '~40%' },
];

const VIGIL_PLANNED = [
  {
    id: 'vg-p1', icon: '🧃', title: 'juice-shop coverage → 80%', priority: 'high',
    desc: '3–5 targeted JS/TS rules to close the gap from 77.4% to 80%, adding ~220 more findings.',
    stories: ['As a rule author, I want juice-shop coverage above 80% so that the rule pack quality benchmark is met.'],
    criteria: ['`vigil benchmark --app juice-shop` reports ≥80%.'],
  },
  {
    id: 'vg-p2', icon: '💎', title: 'railsgoat coverage → 25%', priority: 'high',
    desc: '3–5 Ruby/Rails rules targeting known OWASP patterns missing from current rail packs.',
    stories: ['As a rule author, I want railsgoat coverage above 25% so that Ruby/Rails security rules are production-grade.'],
    criteria: ['`vigil benchmark --app railsgoat` reports ≥25%.'],
  },
  {
    id: 'vg-p3', icon: '🔑', title: '🔑 Vault Domain — Secrets & Credential Leakage', priority: 'medium',
    desc: 'Detects API keys, tokens, passwords, private keys embedded in source. Pattern-match variable names + high-entropy string values. Framework-specific: Django SECRET_KEY, AWS AKIA… keys, GitHub ghp_… tokens, JWT eyJ…',
    stories: [
      'As a developer, I want Vigil to catch hardcoded API keys before they reach git so that secrets never leave my workstation.',
      'As a security auditor, I want to scan all repos for credential leakage so that I can remediate before attackers find them in git history.',
    ],
    criteria: ['Effort: 1–2 days (pattern rules only, no engine work).'],
  },
  {
    id: 'vg-p4', icon: '🔒', title: '🔒 Privacy Domain — PII Tracking', priority: 'medium',
    desc: 'Tracks personal data from source (HTTP request, DB query) to potential leak (log, response, third-party API). Concept mappings: email, phone, SSN, credit card, DOB, address.',
    stories: [
      'As a privacy officer, I want to find where PII flows to third-party APIs so that I can document data transfers for GDPR Article 30.',
      'As a developer, I want Vigil to flag when PII is logged so that I don\'t accidentally leak personal data to log aggregators.',
    ],
    criteria: ['Effort: 2–3 days (+20 concepts for PII data types).'],
  },
  {
    id: 'vg-p5', icon: '🧪', title: '🧪 TestGuard Domain — Trust Your Tests', priority: 'medium',
    desc: 'Detects: always-passing tests, missing assertions, mocked security controls in production code, test fixtures with real credentials.',
    stories: [
      'As a tech lead, I want to detect tests that always pass so that I know our test suite actually validates behavior.',
      'As a security engineer, I want to find security mocks that leaked into production code so that I can remove them.',
    ],
    criteria: ['Effort: 1 day (pattern rules only).'],
  },
  {
    id: 'vg-p6', icon: '🏛️', title: '🏛️ Architect Domain — Enforce Architectural Rules', priority: 'medium',
    desc: 'Define which packages may import which (layered architecture), detect forbidden dependencies and circular imports.',
    stories: [
      'As an architect, I want to enforce that controllers never import repositories directly so that the layered architecture is maintained.',
      'As a developer, I want to detect circular imports so that module boundaries are clear.',
    ],
    criteria: ['Effort: 2–3 days (needs `include.paths` DSL extension).'],
  },
  {
    id: 'vg-p7', icon: '⚙️', title: '⚙️ Craft Domain — Deep Framework Knowledge', priority: 'medium',
    desc: 'Framework anti-patterns baked into rules: Spring, Django, Rails, Express, NestJS. Catches subtle bugs invisible to generic rules.',
    stories: ['As a developer, I want Vigil to catch Spring transaction anti-patterns so that I avoid subtle data consistency bugs.'],
    criteria: ['Effort: 2–3 days per framework pack.'],
  },
  {
    id: 'vg-p8', icon: '🔄', title: '🔄 Migrate Domain — Know What to Upgrade', priority: 'medium',
    desc: 'Detects deprecated API usage + provides precise upgrade instructions per language and framework version.',
    stories: ['As a developer, I want to see all uses of deprecated Java 17 APIs so that I can plan the migration to Java 21.'],
    criteria: ['Effort: 1–2 days per migration pack.'],
  },
  {
    id: 'vg-p9', icon: '⚡', title: '⚡ Perf Domain — Performance Anti-Patterns', priority: 'medium',
    desc: 'N+1 queries, unbounded loops, synchronous I/O in async contexts, inefficient collection operations.',
    stories: ['As a developer, I want Vigil to detect N+1 query patterns so that I catch performance issues before they hit production.'],
    criteria: ['Effort: 3–5 days (loop context needed in `within`).'],
  },
  {
    id: 'vg-p10', icon: '🎓', title: 'Vigil Academy Modules 4–7', priority: 'low',
    desc: '12 new lessons: M4 Precision Engineering, M5 Production Rules, M6 Joern Mastery, M7 Security Engineering (EU CRA compliance rules).',
    stories: [
      'As a security engineer, I want expert Joern lessons so that I can write precise CPG-based taint rules.',
      'As a developer advocate, I want module 7 EU CRA lessons so that developers understand what CRA-compliant code looks like.',
    ],
    criteria: [],
  },
  {
    id: 'vg-p11', icon: '🎨', title: 'Vigil Visual Analyzer', priority: 'low',
    desc: 'Drag-and-drop rule builder. Phase 1: Canvas + 5 node types + live CPGQL preview. Phase 2: Backend executor. Phase 3: Two-way sync + template gallery.',
    stories: [
      'As a security engineer, I want to build taint flow rules visually so that I don\'t need to learn Scala/CPGQL from scratch.',
      'As a rule author, I want to see the CPGQL preview update as I connect nodes so that I understand what the rule does.',
    ],
    criteria: [],
  },
];

/* ── PRISM Native Engine ──────────────────────────────────────────────────── */
const PRISM_PHASES = [
  {
    id: 'prism-1',
    phase: 'Phase 1',
    title: 'Alpha — Pattern Matching',
    timeline: 'Months 1–3',
    icon: '🌱',
    color: '#6366f1',
    bg: 'rgba(99,102,241,.12)',
    target: '10× faster than OpenGrep cold start, 100% pattern rule compatibility',
    desc: 'Native Go tree-sitter integration. AST normalization (CST → language-agnostic nodes). $METAVAR pattern matching + ... ellipsis. Covers 65% of all rules.',
    items: [
      'Native Go tree-sitter (`smacker/go-tree-sitter`)',
      'CST → language-agnostic AST normalization',
      '$METAVAR pattern matching + ... ellipsis',
      '`vigil scan` starts in <100ms',
      'Zero external binary dependencies',
      '65% rule compatibility (pattern, import, class, annotation, function, variable, assignment)',
    ],
    stories: [
      'As a developer, I want `vigil scan` to start in <100ms so that I can run it on every file save.',
      'As a platform operator, I want zero external binary dependencies so that DepGuard deploys as a single Docker image.',
    ],
  },
  {
    id: 'prism-2',
    phase: 'Phase 2',
    title: 'Beta — Taint Analysis',
    timeline: 'Months 3–6',
    icon: '🔬',
    color: '#0ea5e9',
    bg: 'rgba(14,165,233,.12)',
    target: 'Equal precision to OpenGrep, ≤10% more false positives',
    desc: 'Basic block construction + CFG. Intra-procedural then inter-procedural taint engine. Replaces OpenGrep for flow queries.',
    items: [
      'Basic block construction',
      'Control flow graph (CFG)',
      'Intra-procedural taint engine',
      'Inter-procedural taint (call graph required)',
      'Replaces OpenGrep for flow queries',
    ],
    stories: [],
  },
  {
    id: 'prism-3',
    phase: 'Phase 3',
    title: 'v1.0 — Code Property Graph',
    timeline: 'Months 6–12',
    icon: '🧠',
    color: '#22c55e',
    bg: 'rgba(34,197,94,.12)',
    target: '80% of current Joern queries, 5× faster incremental scan',
    desc: 'Full CPG (AST + CFG + PDG unified). SSA form. Type resolution. Persistent incremental CPG in SQLite. Replaces Joern — no more JVM startup.',
    items: [
      'Full CPG: AST + CFG + PDG unified',
      'SSA form (static single assignment)',
      'Type resolution (x.foo() → which method?)',
      'Persistent incremental CPG in SQLite',
      'Only changed files recomputed on rescan',
      'Replaces Joern — no JVM startup penalty',
    ],
    stories: [],
  },
  {
    id: 'prism-4',
    phase: 'Phase 4',
    title: 'v1.5 — SBOM-Aware Analysis',
    timeline: 'Months 12–18',
    icon: '🔗',
    color: '#f59e0b',
    bg: 'rgba(245,158,11,.12)',
    target: 'New class of findings impossible before',
    desc: 'PRISM reads DepGuard\'s SBOM — knows which packages are present and their CVE status. Cross-repo CPG. Compliance policy annotations.',
    items: [
      'PRISM reads DepGuard SBOM for package context',
      'Cross-repo CPG — links repositories in a workspace',
      'Compliance policy annotations on rules (`cra: article-20`, `nist: SI-3`)',
      '"This CVE package is called at this specific line" findings',
    ],
    stories: [],
  },
  {
    id: 'prism-5',
    phase: 'Phase 5',
    title: 'v2.0 — Ecosystem',
    timeline: 'Months 18–24',
    icon: '🚀',
    color: '#ec4899',
    bg: 'rgba(236,72,153,.12)',
    target: 'PRISM is the default for --engine=auto',
    desc: 'Cross-service flow traces data across microservices. External engine adapters archived (legacy fallback only).',
    items: [
      'Cross-service data flow tracing across microservices',
      'Persistent incremental CPG (only recompute changed files)',
      'External engine adapters archived (OpenGrep/Joern/Tree-sitter)',
      'PRISM becomes `--engine=auto` default',
    ],
    stories: [],
  },
];

const PRISM_REUSABLE = [
  { file: 'pkg/dsl/', lines: '2,370', desc: 'Lexer + parser — already parses all VRL queries' },
  { file: 'pkg/concept/', lines: '2,963', desc: 'Concept catalog + mappings — PRISM inherits directly' },
  { file: 'pkg/compiler/', lines: '3,271', desc: 'Shows what PRISM must execute' },
  { file: 'pkg/engine/', lines: '—', desc: 'Engine interface — PRISM just implements Engine' },
];

/* ══════════════════════════════════════════════════════════════════════════ */
/*  HELPERS                                                                   */
/* ══════════════════════════════════════════════════════════════════════════ */
const PRIORITY_META = {
  critical: { label: '🔴 Critical', cls: 'pp-priority-badge--critical', cardCls: 'pp-feature-card--critical' },
  high:     { label: '🟠 High',     cls: 'pp-priority-badge--high',     cardCls: 'pp-feature-card--high'     },
  medium:   { label: '🟡 Medium',   cls: 'pp-priority-badge--medium',   cardCls: 'pp-feature-card--medium'   },
  low:      { label: '🔵 Low',      cls: 'pp-priority-badge--low',       cardCls: 'pp-feature-card--low'      },
};

function StatusBadge({ status }) {
  if (status === 'done')    return <span className="pp-status-badge pp-status-badge--done">✅ Done</span>;
  if (status === 'inprog')  return <span className="pp-status-badge pp-status-badge--inprog">🔄 In Progress</span>;
  return <span className="pp-status-badge pp-status-badge--planned">🔜 Planned</span>;
}

function PriorityBadge({ priority }) {
  const m = PRIORITY_META[priority];
  if (!m) return null;
  return <span className={`pp-status-badge ${m.cls}`}>{m.label}</span>;
}

/* ── Done feature card (compact) ──────────────────────────────────────────── */
function DoneCard({ feature }) {
  return (
    <div className="pp-feature-card pp-feature-card--done">
      <div className="pp-feature-card-header" style={{ cursor: 'default' }}>
        <div className="pp-feature-card-title">
          <span className="pp-feature-icon">{feature.icon}</span>
          <div>
            <div className="pp-feature-name">{feature.title}</div>
            <div className="pp-feature-desc">{feature.desc}</div>
          </div>
        </div>
        <div className="pp-feature-badges">
          <StatusBadge status="done" />
        </div>
      </div>
    </div>
  );
}

/* ── Planned feature card (expandable) ────────────────────────────────────── */
function PlannedCard({ feature, expanded, onToggle }) {
  const pm = PRIORITY_META[feature.priority] || PRIORITY_META.medium;
  return (
    <div className={`pp-feature-card ${pm.cardCls}`}>
      <div className="pp-feature-card-header" onClick={onToggle}>
        <div className="pp-feature-card-title">
          <span className="pp-feature-icon">{feature.icon}</span>
          <div>
            <div className="pp-feature-name">{feature.title}</div>
            <div className="pp-feature-desc">{feature.desc}</div>
          </div>
        </div>
        <div className="pp-feature-badges">
          <PriorityBadge priority={feature.priority} />
          <StatusBadge status="planned" />
          <span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.8rem', marginTop: '0.2rem' }}>
            {expanded ? '▲' : '▼'}
          </span>
        </div>
      </div>
      {expanded && (
        <div className="pp-feature-body">
          {feature.stories && feature.stories.length > 0 && (
            <div className="pp-stories-section">
              <div className="pp-section-label">User Stories</div>
              {feature.stories.map((s, i) => (
                <div key={i} className="pp-story">
                  <span className="pp-story-icon">💬</span>
                  <span>{s}</span>
                </div>
              ))}
            </div>
          )}
          {feature.criteria && feature.criteria.length > 0 && (
            <div className="pp-criteria-section">
              <div className="pp-section-label">Acceptance Criteria</div>
              {feature.criteria.map((c, i) => (
                <div key={i} className="pp-criterion">
                  <span className="pp-criterion-icon">✓</span>
                  <span>{c}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  TAB CONTENT                                                               */
/* ══════════════════════════════════════════════════════════════════════════ */

function DepGuardTab() {
  const [doneExpanded, setDoneExpanded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all');

  const total = DEPGUARD_DONE.length + DEPGUARD_PLANNED.length;
  const pct = Math.round((DEPGUARD_DONE.length / total) * 100);

  const filtered = filter === 'done' ? [] : DEPGUARD_PLANNED.filter(f => {
    if (filter === 'all') return true;
    return f.priority === filter;
  });

  const tierGroups = [
    { label: '🔴 Critical Priority', key: 'critical' },
    { label: '🟠 High Priority',     key: 'high'     },
    { label: '🟡 Medium Priority',   key: 'medium'   },
  ];

  return (
    <>
      {/* Overview */}
      <div className="pp-project-overview">
        <div>
          <h2>🛡️ DepGuard Platform</h2>
          <p>
            EU CRA-focused SBOM &amp; Dependency Security Platform. Production status with 31 shipped features.
            Vulnerability scanning, license compliance, policy enforcement, AI triage, and supply chain intelligence.
          </p>
        </div>
        <div className="pp-overview-meta">
          <div className="pp-overview-stats">
            <div className="pp-overview-stat"><strong style={{color:'#22c55e'}}>{DEPGUARD_DONE.length}</strong><span>Done</span></div>
            <div className="pp-overview-stat"><strong style={{color:'#6366f1'}}>{DEPGUARD_PLANNED.length}</strong><span>Planned</span></div>
            <div className="pp-overview-stat"><strong>{total}</strong><span>Total</span></div>
          </div>
          <div className="pp-progress-bar-wrap">
            <div className="pp-progress-bar-label">{pct}% complete</div>
            <div className="pp-progress-bar"><div className="pp-progress-bar-fill" style={{width: `${pct}%`}} /></div>
          </div>
        </div>
      </div>

      {/* Completed */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>✅ Completed</h3>
          <span className="pp-tier-count">{DEPGUARD_DONE.length} features</span>
        </div>
        <button className="pp-collapse-toggle" onClick={() => setDoneExpanded(v => !v)}>
          <span>{doneExpanded ? 'Hide' : 'Show'} completed features</span>
          <span className={`pp-collapse-chevron${doneExpanded ? ' pp-collapse-chevron--open' : ''}`}>▼</span>
        </button>
        {doneExpanded && (
          <div className="pp-feature-grid">
            {DEPGUARD_DONE.map(f => <DoneCard key={f.id} feature={f} />)}
          </div>
        )}
      </div>

      {/* Planned */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>🔜 Planned</h3>
          <span className="pp-tier-count">{DEPGUARD_PLANNED.length} features</span>
        </div>
        <div className="pp-filter-bar">
          {['all','critical','high','medium'].map(f => (
            <button key={f} className={`pp-filter-btn${filter === f ? ' pp-filter-btn--active' : ''}`} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : PRIORITY_META[f].label}
            </button>
          ))}
        </div>
        {tierGroups.map(g => {
          const items = filter === 'all' ? DEPGUARD_PLANNED.filter(f => f.priority === g.key) : (filter === g.key ? filtered : []);
          if (items.length === 0 && filter !== 'all') return null;
          const display = filter === 'all' ? DEPGUARD_PLANNED.filter(f => f.priority === g.key) : filtered;
          if (filter !== 'all' && filter !== g.key) return null;
          return (
            <div key={g.key} style={{marginBottom:'1.5rem'}}>
              <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--bs-secondary-color)',marginBottom:'0.75rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
                {g.label}
              </div>
              <div className="pp-feature-grid">
                {display.map(f => (
                  <PlannedCard
                    key={f.id}
                    feature={f}
                    expanded={expandedId === f.id}
                    onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

function VigilTab() {
  const [doneExpanded, setDoneExpanded] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const total = VIGIL_DONE.length + VIGIL_PLANNED.length;
  const pct = Math.round((VIGIL_DONE.length / total) * 100);

  return (
    <>
      {/* Overview */}
      <div className="pp-project-overview">
        <div>
          <h2>⚡ Vigil SAST Engine</h2>
          <p>
            Best-in-class open-source SAST with 110+ rule packs, 10 languages, multi-engine architecture.
            Production v1 — OpenGrep/Semgrep + Joern CPG + Tree-sitter. Vigil DSL with full grammar.
            Rule Studio, Academy, Concept catalog.
          </p>
        </div>
        <div className="pp-overview-meta">
          <div className="pp-overview-stats">
            <div className="pp-overview-stat"><strong style={{color:'#22c55e'}}>{VIGIL_DONE.length}</strong><span>Done</span></div>
            <div className="pp-overview-stat"><strong style={{color:'#6366f1'}}>{VIGIL_PLANNED.length}</strong><span>Planned</span></div>
            <div className="pp-overview-stat"><strong>{total}</strong><span>Total</span></div>
          </div>
          <div className="pp-progress-bar-wrap">
            <div className="pp-progress-bar-label">{pct}% complete</div>
            <div className="pp-progress-bar"><div className="pp-progress-bar-fill" style={{width: `${pct}%`}} /></div>
          </div>
        </div>
      </div>

      {/* OWASP Coverage */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>📊 OWASP Benchmark Coverage</h3>
        </div>
        <div className="pp-coverage-grid">
          {VIGIL_COVERAGE.map(c => {
            const ratio = Math.min(c.current / c.target, 1);
            const color = ratio >= 0.95 ? '#22c55e' : ratio >= 0.8 ? '#f59e0b' : '#6366f1';
            return (
              <div key={c.name} className="pp-coverage-card">
                <div className="pp-coverage-name">{c.name} <span style={{color:'var(--bs-secondary-color)',fontWeight:400,fontSize:'0.78rem'}}>({c.findings})</span></div>
                <div className="pp-coverage-bar-wrap">
                  <div className="pp-coverage-bar">
                    <div className="pp-coverage-bar-fill" style={{width:`${c.current}%`, background: color}} />
                  </div>
                  <div className="pp-coverage-pct" style={{color}}>
                    {c.current}%
                    <span style={{color:'var(--bs-secondary-color)',fontWeight:400,fontSize:'0.72rem',display:'block'}}>target {c.target}%</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Completed */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>✅ Completed</h3>
          <span className="pp-tier-count">{VIGIL_DONE.length} features</span>
        </div>
        <button className="pp-collapse-toggle" onClick={() => setDoneExpanded(v => !v)}>
          <span>{doneExpanded ? 'Hide' : 'Show'} completed features</span>
          <span className={`pp-collapse-chevron${doneExpanded ? ' pp-collapse-chevron--open' : ''}`}>▼</span>
        </button>
        {doneExpanded && (
          <div className="pp-feature-grid">
            {VIGIL_DONE.map(f => <DoneCard key={f.id} feature={f} />)}
          </div>
        )}
      </div>

      {/* Near-term — Coverage */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>🔜 Planned</h3>
          <span className="pp-tier-count">{VIGIL_PLANNED.length} features</span>
        </div>

        <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--bs-secondary-color)',marginBottom:'0.75rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
          🟠 Near-term — Coverage Targets
        </div>
        <div className="pp-feature-grid" style={{marginBottom:'1.5rem'}}>
          {VIGIL_PLANNED.filter(f => f.priority === 'high').map(f => (
            <PlannedCard key={f.id} feature={f} expanded={expandedId === f.id} onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
          ))}
        </div>

        <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--bs-secondary-color)',marginBottom:'0.75rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
          🟡 Next Quarter — 7 New Scan Domains
        </div>
        <div className="pp-feature-grid" style={{marginBottom:'1.5rem'}}>
          {VIGIL_PLANNED.filter(f => f.priority === 'medium').map(f => (
            <PlannedCard key={f.id} feature={f} expanded={expandedId === f.id} onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
          ))}
        </div>

        <div style={{fontSize:'0.88rem',fontWeight:700,color:'var(--bs-secondary-color)',marginBottom:'0.75rem',textTransform:'uppercase',letterSpacing:'0.05em'}}>
          🔵 Future — Academy &amp; Visual Tools
        </div>
        <div className="pp-feature-grid">
          {VIGIL_PLANNED.filter(f => f.priority === 'low').map(f => (
            <PlannedCard key={f.id} feature={f} expanded={expandedId === f.id} onToggle={() => setExpandedId(expandedId === f.id ? null : f.id)} />
          ))}
        </div>
      </div>
    </>
  );
}

function PrismTab() {
  const [expandedId, setExpandedId] = useState(null);

  return (
    <>
      {/* Overview */}
      <div className="pp-project-overview">
        <div>
          <h2>🔬 PRISM Native Engine</h2>
          <p>
            Long-term vision to replace OpenGrep + Joern + Tree-sitter + Rascal with a single Go binary.
            A prism refracts a single beam of light into its full spectrum — PRISM does the same with source code.
            5 phases over 24 months.
          </p>
        </div>
        <div className="pp-overview-meta">
          <div className="pp-overview-stats">
            <div className="pp-overview-stat"><strong style={{color:'#22c55e'}}>0</strong><span>Done</span></div>
            <div className="pp-overview-stat"><strong style={{color:'#f59e0b'}}>1</strong><span>In Design</span></div>
            <div className="pp-overview-stat"><strong style={{color:'#6366f1'}}>5</strong><span>Phases</span></div>
          </div>
          <div className="pp-progress-bar-wrap">
            <div className="pp-progress-bar-label">Design phase</div>
            <div className="pp-progress-bar"><div className="pp-progress-bar-fill" style={{width:'5%'}} /></div>
          </div>
        </div>
      </div>

      {/* Why PRISM */}
      <div className="pp-tier">
        <div className="pp-tier-header"><h3>❓ Why PRISM?</h3></div>
        <div style={{
          background:'var(--bs-body-bg)',
          border:'1px solid var(--bs-border-color)',
          borderRadius:'var(--radius-card,12px)',
          padding:'1.25rem 1.5rem',
          marginBottom:'1.5rem',
          fontSize:'0.9rem',
          lineHeight:1.65,
        }}>
          <p style={{margin:'0 0 0.75rem'}}>
            Every engine call today is a <strong>subprocess exec + multi-second startup penalty</strong>.
            Users see "scan timed out" on large repositories. PRISM delivers ≥90% capability with zero external dependencies.
          </p>
          <p style={{margin:0}}>
            By implementing the full analysis pipeline in native Go, DepGuard can ship as a single binary,
            start scans in milliseconds, and perform incremental analysis on only changed files.
          </p>
        </div>

        {/* Reusable code */}
        <div className="pp-tier-header"><h3>♻️ Already Reusable from Vigil</h3></div>
        <div style={{
          background:'var(--bs-body-bg)',
          border:'1px solid var(--bs-border-color)',
          borderRadius:'var(--radius-card,12px)',
          overflow:'hidden',
          marginBottom:'2rem',
        }}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:'0.88rem',fontFamily:'var(--font-sans)'}}>
            <thead>
              <tr style={{borderBottom:'1px solid var(--bs-border-color)',background:'var(--bs-tertiary-bg,rgba(0,0,0,.04))'}}>
                <th style={{padding:'0.65rem 1rem',fontWeight:600,textAlign:'left',color:'var(--bs-secondary-color)',fontSize:'0.78rem',textTransform:'uppercase',letterSpacing:'0.04em'}}>Package</th>
                <th style={{padding:'0.65rem 1rem',fontWeight:600,textAlign:'right',color:'var(--bs-secondary-color)',fontSize:'0.78rem',textTransform:'uppercase',letterSpacing:'0.04em'}}>Lines</th>
                <th style={{padding:'0.65rem 1rem',fontWeight:600,textAlign:'left',color:'var(--bs-secondary-color)',fontSize:'0.78rem',textTransform:'uppercase',letterSpacing:'0.04em'}}>Role in PRISM</th>
              </tr>
            </thead>
            <tbody>
              {PRISM_REUSABLE.map((r, i) => (
                <tr key={r.file} style={{borderBottom: i < PRISM_REUSABLE.length - 1 ? '1px solid var(--bs-border-color)' : 'none'}}>
                  <td style={{padding:'0.75rem 1rem',fontFamily:'var(--font-mono)',fontWeight:600,color:'var(--accent)'}}>{r.file}</td>
                  <td style={{padding:'0.75rem 1rem',textAlign:'right',fontWeight:700}}>{r.lines}</td>
                  <td style={{padding:'0.75rem 1rem',color:'var(--bs-secondary-color)'}}>{r.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Phase Timeline */}
      <div className="pp-tier">
        <div className="pp-tier-header">
          <h3>🗓️ 5-Phase Roadmap</h3>
          <span className="pp-tier-count">24 months</span>
        </div>
        <div className="pp-phase-timeline">
          {PRISM_PHASES.map(ph => (
            <div key={ph.id}>
              <div
                className="pp-phase-item"
                onClick={() => setExpandedId(expandedId === ph.id ? null : ph.id)}
                style={{borderLeft: `4px solid ${ph.color}`}}
              >
                <div className="pp-phase-dot" style={{background: ph.bg, color: ph.color}}>
                  {ph.icon}
                </div>
                <div className="pp-phase-content">
                  <h4>{ph.phase} — {ph.title}</h4>
                  <p>{ph.desc}</p>
                </div>
                <div className="pp-phase-meta">
                  <span className="pp-phase-timeline-tag">{ph.timeline}</span>
                  <span className="pp-phase-target" style={{color: ph.color, fontWeight:600, fontSize:'0.78rem'}}>🎯 {ph.target}</span>
                </div>
              </div>
              {expandedId === ph.id && (
                <div className="pp-phase-body">
                  <ul className="pp-phase-checklist">
                    {ph.items.map((item, i) => <li key={i}>{item}</li>)}
                  </ul>
                  {ph.stories.length > 0 && (
                    <div style={{marginTop:'1rem'}}>
                      <div className="pp-section-label">User Stories</div>
                      {ph.stories.map((s, i) => (
                        <div key={i} className="pp-story">
                          <span className="pp-story-icon">💬</span>
                          <span>{s}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
/*  MAIN PAGE                                                                 */
/* ══════════════════════════════════════════════════════════════════════════ */
const TABS = [
  { label: '🛡️ DepGuard Platform', Component: DepGuardTab },
  { label: '⚡ Vigil SAST Engine', Component: VigilTab    },
  { label: '🔬 PRISM Native Engine', Component: PrismTab  },
];

const GLOBAL_STATS = {
  done:    DEPGUARD_DONE.length + VIGIL_DONE.length,
  inprog:  1, // PRISM design
  planned: DEPGUARD_PLANNED.length + VIGIL_PLANNED.length + PRISM_PHASES.length,
  total:   DEPGUARD_DONE.length + DEPGUARD_PLANNED.length + VIGIL_DONE.length + VIGIL_PLANNED.length + PRISM_PHASES.length + 1,
};

export default function ProductPlan() {
  const [activeTab, setActiveTab] = useState(0);
  const { Component } = TABS[activeTab];

  return (
    <div className="pp-page">
      {/* Hero */}
      <div className="pp-hero">
        <h1>📋 Product Roadmap</h1>
        <p>Full development plan across all three products — DepGuard Platform, Vigil SAST Engine, and PRISM Native Engine.</p>
        <div className="pp-kpi-row">
          <div className="pp-kpi-card pp-kpi-card--done">
            <div className="pp-kpi-card__value">{GLOBAL_STATS.done}</div>
            <div className="pp-kpi-card__label">Features Shipped</div>
          </div>
          <div className="pp-kpi-card pp-kpi-card--prog">
            <div className="pp-kpi-card__value">{GLOBAL_STATS.inprog}</div>
            <div className="pp-kpi-card__label">In Design</div>
          </div>
          <div className="pp-kpi-card pp-kpi-card--plan">
            <div className="pp-kpi-card__value">{GLOBAL_STATS.planned}</div>
            <div className="pp-kpi-card__label">Planned Features</div>
          </div>
          <div className="pp-kpi-card pp-kpi-card--total">
            <div className="pp-kpi-card__value">{GLOBAL_STATS.total}</div>
            <div className="pp-kpi-card__label">Total Scope</div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="pp-tabs">
        {TABS.map((tab, i) => (
          <button
            key={i}
            className={`pp-tab-btn${activeTab === i ? ' pp-tab-btn--active' : ''}`}
            onClick={() => setActiveTab(i)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <Component />
    </div>
  );
}
